import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalog,
  expandDeckDefinition,
  validateDeck,
  type CardCatalog,
  type DeckDefinition,
} from "@fate-kingdom/card-data";

import { TEST_CARD_CATALOG, TEST_LAND_DEFINITION_IDS } from "./setup-card-fixtures.js";

export const COMMAND_TEST_CARD_DATA_VERSION = "0.4.0-command.1";
export const TEST_COMMANDER_DEFINITION_ID = "card.test-command-commander";
export const TEST_COMMAND_CONSPIRACY_DEFINITION_ID = "card.test-command-conspiracy";
const TEST_COMMANDER_MANDATE_CONDITION_ID = "condition.test-command-mandate";
const TEST_COMMANDER_MANDATE_EFFECT_ID = "effect.test-command-mandate";
const TEST_COMMAND_CONSPIRACY_EFFECT_ID = "effect.test-command-conspiracy";
const TEST_COMMAND_CONSPIRACY_TRIGGER_ID = "trigger.test-command-conspiracy";
export const TEST_COMMAND_ARMY_DEFINITION_IDS = {
  vanguard: "card.test-command-vanguard",
  sentries: "card.test-command-sentries",
  outriders: "card.test-command-outriders",
  wardens: "card.test-command-wardens",
  scouts: "card.test-command-scouts",
} as const;

function makeCommandArmy(
  id: string,
  name: string,
  stats: { readonly attack: number; readonly guard: number },
) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "army",
    name,
    cost: {},
    rulesText: "TEST ONLY: zero-Upkeep Army used by deterministic Command scenarios.",
    stats: {
      armySize: 1,
      attack: stats.attack,
      guard: stats.guard,
    },
    upkeep: {},
    effects: [],
  };
}

/**
 * A separate test-only catalog for pre-supplied Command checkpoints. The
 * established setup/Supply catalog must remain byte-for-byte frozen because
 * its hash anchors historical replay fixtures.
 */
export const COMMAND_TEST_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  ...TEST_CARD_CATALOG,
  cardDataVersion: COMMAND_TEST_CARD_DATA_VERSION,
  cards: [
    ...TEST_CARD_CATALOG.cards,
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMANDER_DEFINITION_ID,
      type: "commander",
      name: "TEST ONLY Command Commander",
      cost: {},
      rulesText: "TEST ONLY: Commander used by deterministic Command scenarios.",
      stats: {
        attack: 2,
        guard: 1,
        armyCapacity: 5,
      },
      mandates: [
        {
          mandateId: "mandate.test-command",
          name: "TEST ONLY Command Mandate",
          condition: { conditionId: TEST_COMMANDER_MANDATE_CONDITION_ID },
          effects: [{ effectId: TEST_COMMANDER_MANDATE_EFFECT_ID }],
        },
      ],
    },
    makeCommandArmy(TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard, "TEST ONLY Vanguard", {
      attack: 2,
      guard: 1,
    }),
    makeCommandArmy(TEST_COMMAND_ARMY_DEFINITION_IDS.sentries, "TEST ONLY Sentries", {
      attack: 1,
      guard: 2,
    }),
    makeCommandArmy(TEST_COMMAND_ARMY_DEFINITION_IDS.outriders, "TEST ONLY Outriders", {
      attack: 2,
      guard: 0,
    }),
    makeCommandArmy(TEST_COMMAND_ARMY_DEFINITION_IDS.wardens, "TEST ONLY Wardens", {
      attack: 0,
      guard: 3,
    }),
    makeCommandArmy(TEST_COMMAND_ARMY_DEFINITION_IDS.scouts, "TEST ONLY Scouts", {
      attack: 1,
      guard: 1,
    }),
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMAND_CONSPIRACY_DEFINITION_ID,
      type: "conspiracy",
      name: "TEST ONLY Command Conspiracy",
      cost: {},
      rulesText: "TEST ONLY: inert Conspiracy used to prove garrison preservation.",
      trigger: { triggerId: TEST_COMMAND_CONSPIRACY_TRIGGER_ID },
      resolution: {
        effects: [{ effectId: TEST_COMMAND_CONSPIRACY_EFFECT_ID }],
        destination: "discard",
      },
    },
  ],
  conditions: [
    ...TEST_CARD_CATALOG.conditions,
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMANDER_MANDATE_CONDITION_ID,
      rulesText: "TEST ONLY: inert Command fixture condition.",
    },
  ],
  effects: [
    ...TEST_CARD_CATALOG.effects,
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMANDER_MANDATE_EFFECT_ID,
      rulesText: "TEST ONLY: inert Command fixture effect.",
    },
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMAND_CONSPIRACY_EFFECT_ID,
      rulesText: "TEST ONLY: inert Command Conspiracy effect.",
    },
  ],
  triggers: [
    ...TEST_CARD_CATALOG.triggers,
    {
      schemaVersion: 1,
      definitionVersion: 1,
      id: TEST_COMMAND_CONSPIRACY_TRIGGER_ID,
      rulesText: "TEST ONLY: inert Command Conspiracy trigger.",
    },
  ],
});

export const COMMAND_TEST_ENGINE_CATALOG = createEngineGameplayCatalog(COMMAND_TEST_CARD_CATALOG);

const commandDeckInput = DeckDefinitionSchema.parse({
  schemaVersion: 1,
  definitionVersion: 1,
  cardDataVersion: COMMAND_TEST_CARD_DATA_VERSION,
  id: "deck.test-command-disband",
  name: "TEST ONLY: supplied Command disband deck",
  cards: [
    { cardId: TEST_LAND_DEFINITION_IDS.plains, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.mountain, count: 3 },
    { cardId: TEST_LAND_DEFINITION_IDS.river, count: 3 },
    { cardId: TEST_COMMANDER_DEFINITION_ID, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.vanguard, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.sentries, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.outriders, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.wardens, count: 3 },
    { cardId: TEST_COMMAND_ARMY_DEFINITION_IDS.scouts, count: 3 },
    { cardId: "card.test-filler-army-01", count: 3 },
    { cardId: "card.test-filler-army-02", count: 3 },
    { cardId: "card.test-filler-army-03", count: 3 },
    { cardId: "card.test-filler-army-04", count: 3 },
    { cardId: TEST_COMMAND_CONSPIRACY_DEFINITION_ID, count: 1 },
  ],
});

const validatedCommandDeck = validateDeck(commandDeckInput, COMMAND_TEST_CARD_CATALOG);
if (!validatedCommandDeck.success) {
  throw new Error(`Invalid test-only Command deck: ${validatedCommandDeck.error.message}`);
}

export const COMMAND_TEST_DECK: DeckDefinition = validatedCommandDeck.data;
export const COMMAND_TEST_DECK_CARD_IDS = expandDeckDefinition(COMMAND_TEST_DECK);
